import React, { useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import EditableInput from './EditableInput';
import { CiEdit } from "react-icons/ci";
import { FaMagic, FaSpinner, FaMapMarkedAlt } from 'react-icons/fa';
import '../../styles/components/itinerary/ItineraryHeader.css';
import { usePlaceContext } from './PlaceContext';
import { validateTextInput } from '../common/TextValidator';
import useItineraryInteraction from '../itinerary/useItineraryInteraction';
import InteractionButtons from '../common/InteractionButtons';
import ReportButton from '../common/ReportButton';
import { ContentType } from '../../utils/enums';
import AdditionalInfo from '../common/AdditionalInfo';

const MAX_TITLE_LENGTH = 60;
const MIN_TITLE_LENGTH = 1;

const ItineraryHeader = ({
  onCreateNew,
  onRenameItinerary,
  onDeleteItinerary,
  onUpdatePrivacy,
  isCreatingItinerary,
  isRenamingItinerary,
  isDeletingItinerary,
  isUpdatingPrivacy,
  activeSection,
  setActiveSection,
  readOnly,
  additionalInfoData,
  enableTagSelector = false,
  onAdditionalTagsChange,
}) => {
  const { setSelectedItinerary, state } = usePlaceContext();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [isNameUpdating, setIsNameUpdating] = useState(false);
  const inputRef = useRef(null);
  const selectedItinerary = state.selectedItinerary;
  const interactionProps = useItineraryInteraction(selectedItinerary?.id, selectedItinerary);

  useEffect(() => {
    if (state.selectedItinerary) {
      setNewName(state.selectedItinerary.name || '');
      setValidationErrors([]);
    } else {
      setNewName('');
    }
  }, [state.selectedItinerary]);

  const handleEditToggle = useCallback(() => {
    setIsEditing(true);
    setValidationErrors([]);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const handleRename = useCallback(async () => {
    if (!state.selectedItinerary) return;
    if (validationErrors.length > 0) return;
    if (newName.trim() !== '' && newName !== state.selectedItinerary.name) {
      setIsNameUpdating(true);
      try {
        await onRenameItinerary(state.selectedItinerary.id, newName);
        setSelectedItinerary({ ...state.selectedItinerary, name: newName });
      } catch (error) {
        console.error("Failed to rename itinerary:", error);
        setValidationErrors(['Failed to rename itinerary. Please try again.']);
      }
      setIsNameUpdating(false);
    }
    setIsEditing(false);
  }, [newName, state.selectedItinerary, onRenameItinerary, validationErrors, setSelectedItinerary]);

  const handleBlur = useCallback(() => {
    handleRename();
  }, [handleRename]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleRename();
    }
  }, [handleRename]);

  const handleNameChange = (e) => {
    const inputVal = e.target.value;
    const { errors, sanitizedText } = validateTextInput(inputVal, {
      maxLength: MAX_TITLE_LENGTH,
      minLength: MIN_TITLE_LENGTH,
      allowSpecialChars: true,
      allowedSpecialChars: ".,!?-_'\"<>&",
      profanity_check: true
    });
    if (errors.length > 0) {
      setValidationErrors(errors);
    } else {
      setValidationErrors([]);
    }
    setNewName(sanitizedText);
  };

  if (readOnly) {
    return (
      <div className="itinerary-header">
        <div className="header-main-content">
          {state.selectedItinerary && (
            <>
              <div className="itinerary-name-section">
                <h1 className="itinerary-name" title={state.selectedItinerary.name}>
                  {state.selectedItinerary.name}
                </h1>
              </div>
              {additionalInfoData && (
                <AdditionalInfo
                  data={{
                    ...additionalInfoData,
                    thumbnail_url: state.selectedItinerary.thumbnail_url || '',
                    itineraryId: state.selectedItinerary.id
                  }}
                  enableTagSelector={enableTagSelector}
                  onTagsChange={onAdditionalTagsChange}
                />
              )}
              <div className="itinerary-interactions-row">
                <div className="interaction-buttons-container">
                  <InteractionButtons
                    userInteraction={interactionProps.userInteraction}
                    handleLike={interactionProps.likeItinerary}
                    handleDislike={interactionProps.dislikeItinerary}
                    interactionLoading={interactionProps.interactionLoading}
                    likeCount={state.selectedItinerary.like_count || 0}
                    dislikeCount={state.selectedItinerary.dislike_count || 0}
                    viewCount={state.selectedItinerary.view_count || 0}
                  />
                </div>
                <div className="report-buttons-container">
                  <ReportButton contentId={state.selectedItinerary.id} contentType={ContentType.ITINERARY} />
                </div>
              </div>
              <div className="affiliate-disclosure">This guide may contain affiliate links. If you click these links and make a purchase, we may earn a commission at no additional cost to you.</div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!state.selectedItinerary) {
    return (
      <div className="itinerary-header">
        <div className="header-main-content">
          <div className="no-itinerary">
            <p>You either don't have access or the itinerary cannot be found</p>
            <button className="create-itinerary-button" onClick={onCreateNew}>Create New Itinerary</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="itinerary-header">
      <div className="header-main-content">
        <div className="itinerary-header-grid">
          <div className="name-area">
            <div className="itinerary-name-section">
              {!isEditing ? (
                <div className="itinerary-name-display">
                  <span className={`itinerary-name ${isNameUpdating ? 'updating' : ''}`} title={state.selectedItinerary.name}>
                    {state.selectedItinerary.name}
                  </span>
                  <CiEdit
                    className="action-icon edit-icon"
                    onClick={handleEditToggle}
                    role="button"
                    tabIndex={0}
                    aria-label="Rename Itinerary"
                    title="Rename Itinerary"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleEditToggle(); }}
                  />
                  {isDeletingItinerary && <FaSpinner className="spinner delete-spinner" aria-label="Deleting itinerary" />}
                </div>
              ) : (
                <div className="editing-container">
                  <div className="input-wrapper">
                    <EditableInput
                      value={newName || ""}
                      onChange={handleNameChange}
                      onBlur={handleBlur}
                      onKeyDown={handleKeyDown}
                      inputRef={inputRef}
                      disabled={isRenamingItinerary}
                      className={`itinerary-edit-input ${validationErrors.length > 0 ? 'error' : ''}`}
                      aria-label="Edit Itinerary Name"
                    />
                    {isRenamingItinerary && <FaSpinner className="spinner editing-spinner" aria-label="Renaming itinerary" />}
                    <div className="itinerary-header-character-count">
                      {newName.length}/{MAX_TITLE_LENGTH}
                    </div>
                  </div>
                  <div className={`error-messages ${validationErrors.length > 0 ? 'visible' : ''}`}>
                    {validationErrors.map((err, index) => (
                      <div key={index} className="error-text">{err}</div>
                    ))}
                  </div>
                </div>
              )}
              {isNameUpdating && (
                <div className="name-updating-spinner">
                  <FaSpinner className="spinner inline-spinner" aria-label="Updating itinerary name" />
                </div>
              )}
            </div>
          </div>
        </div>
        {additionalInfoData && (
          <AdditionalInfo
            data={{
              ...additionalInfoData,
              thumbnail_url: state.selectedItinerary.thumbnail_url || '',
              itineraryId: state.selectedItinerary.id
            }}
            enableTagSelector={enableTagSelector}
            onTagsChange={onAdditionalTagsChange}
          />
        )}
        <div className="travel-assistant-button-container">
          <button
            type="button"
            className="travel-assistant-button"
            onClick={() => setActiveSection(activeSection === 'itinerary' ? 'chatbot' : 'itinerary')}
          >
            {activeSection === 'itinerary' ? (<><FaMagic /> Itinerary Assistant</>) : (<><FaMapMarkedAlt /> View Itinerary</>)}
          </button>
        </div>
      </div>
    </div>
  );
};

ItineraryHeader.propTypes = {
  onCreateNew: PropTypes.func.isRequired,
  onRenameItinerary: PropTypes.func.isRequired,
  onDeleteItinerary: PropTypes.func.isRequired,
  onUpdatePrivacy: PropTypes.func.isRequired,
  isCreatingItinerary: PropTypes.bool.isRequired,
  isRenamingItinerary: PropTypes.bool.isRequired,
  isDeletingItinerary: PropTypes.bool.isRequired,
  isUpdatingPrivacy: PropTypes.bool.isRequired,
  activeSection: PropTypes.string.isRequired,
  setActiveSection: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  additionalInfoData: PropTypes.shape({
    destinationName: PropTypes.string,
    destinationLink: PropTypes.string,
    authorName: PropTypes.string,
    updatedDate: PropTypes.string,
    itineraryLink: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    thumbnail_url: PropTypes.string,
    itineraryId: PropTypes.string,
  }),
  enableTagSelector: PropTypes.bool,
  onAdditionalTagsChange: PropTypes.func,
};

export default ItineraryHeader;
